import { isString } from 'lodash';

import { ClientApplicationStages } from 'src/utils/applications-kanban';

import i18n from 'src/locales/i18n';

import { TenantType } from 'src/types/enums';
import { BusinessErrorCodes } from 'src/types/business-errors';

import { applicationsApi } from './applications.service';
import { JobApplication, RejectionReason, ApplicationStage } from './applications.types';

export const optimisticallyProgressCandidateApplicationStatus = (
  jobId: string,
  applicationId: string,
  stage: string
) =>
  applicationsApi.util.updateQueryData(
    'getJobCandidates',
    {
      jobId,
      params: {
        page: 1,
        per_page: 999,
      },
    },
    (applicationsResponse) => ({
      ...applicationsResponse,
      results: applicationsResponse.results.map((application: JobApplication) =>
        application.id === applicationId
          ? { ...application, stage: stage as ApplicationStage }
          : application
      ),
    }),
    true
  );

export const isApplicationFinalized = (stage: ApplicationStage) =>
  [
    ApplicationStage.REJECTED,
    ApplicationStage.WITHDRAWN,
    ApplicationStage.TO_BE_REJECTED,
    ApplicationStage.OFFER_ACCEPTED,
  ].includes(stage);

export const getConfirmStageChangeContentMessage = (
  targetStage: ApplicationStage,
  tenantType: TenantType,
  isShortlisted: boolean = false
) => {
  const targetStageLabel = i18n.t(`enums.application_stage.${targetStage}`);

  if (targetStage === ApplicationStage.REJECTED) {
    return i18n.t('applications.stage_change.reject_application');
  }

  if (targetStage === ApplicationStage.SHORTLISTED && tenantType === TenantType.Recruiter) {
    return i18n.t('applications.stage_change.confirm_shortlist_recruiter');
  }

  if (targetStage === ApplicationStage.SHORTLISTED && tenantType === TenantType.Client) {
    return i18n.t('applications.stage_change.confirm_shortlist_client');
  }

  if (ClientApplicationStages.includes(targetStage) && tenantType === TenantType.Recruiter) {
    if (isShortlisted) {
      return i18n.t('applications.stage_change.confirm_alert_client', {
        target: targetStageLabel,
      });
    }
    if (!isShortlisted && ![ApplicationStage.REJECTED, ApplicationStage.TO_BE_REJECTED, ApplicationStage.WITHDRAWN].includes(targetStage)) {
      return i18n.t('applications.stage_change.confirm_alert_both', {
        target: targetStageLabel,
      });
    }

    return i18n.t('applications.stage_change.confirm', {
      target: targetStageLabel,
    });

  }

  if (ClientApplicationStages.includes(targetStage) && tenantType === TenantType.Client) {
    return i18n.t('applications.stage_change.confirm', {
      target: targetStageLabel,
    });
  }

  return i18n.t('applications.stage_change.confirm', {
    target: targetStageLabel,
  });
};

export const getConfirmStageChangeRequestContentMessage = (targetStage: ApplicationStage) => {

  const targetStageLabel = i18n.t(`enums.application_stage.${targetStage}`);

  return i18n.t('applications.stage_change_request.confirm', {
    target: targetStageLabel,
  });
};

export const UserManagedRejectionReasons = [
  {
    value: RejectionReason.NOT_GOOD_FIT,
    label: i18n.t('applications.rejection.reasons.not_good_fit'),
  },
  {
    value: RejectionReason.ROLE_CLOSED,
    label: i18n.t('applications.rejection.reasons.role_closed'),
  },
  {
    value: RejectionReason.NOT_ENOUGH_EXPERIENCE,
    label: i18n.t('applications.rejection.reasons.not_enough_experience'),
  },
  {
    value: RejectionReason.NOT_ENOUGH_QUALIFICATIONS,
    label: i18n.t('applications.rejection.reasons.not_enough_qualifications'),
  },
  {
    value: RejectionReason.SKILL_MISMATCH,
    label: i18n.t('applications.rejection.reasons.skill_mismatch'),
  },
  {
    value: RejectionReason.SALARY_EXPECTATIONS_MISMATCH,
    label: i18n.t('applications.rejection.reasons.salary_expectations_mismatch'),
  },
  {
    value: RejectionReason.AVAILABILITY_ISSUES,
    label: i18n.t('applications.rejection.reasons.availability_issues'),
  },
  {
    value: RejectionReason.LOCATION_ISSUES,
    label: i18n.t('applications.rejection.reasons.location_issues'),
  },
  {
    value: RejectionReason.ROLE_FILLED,
    label: i18n.t('applications.rejection.reasons.role_filled'),
  },
  {
    value: RejectionReason.OTHER,
    label: i18n.t('applications.rejection.reasons.other')
  },
]

export const getUpdateStageErrorMessage = (e: any) => {
  if (e?.status === 403) {
    return i18n.t('exceptions.unauthorized');
  }

  if (e?.data?.error_code === BusinessErrorCodes.JobNotVerified) {
    return i18n.t('applications.api.update_stage.not_verified');
  }
  if (e?.data?.error_code === BusinessErrorCodes.ShortlistLimitReached) {
    return i18n.t('applications.api.update_stage.shortlist_limit_reached');
  }
  if (e?.data?.error_code === BusinessErrorCodes.ShortlistDeadlineExpired) {
    return i18n.t('applications.api.update_stage.shortlist_deadline_expired');
  }
  if (e?.data?.error_code === BusinessErrorCodes.InvalidStageProgression) {
    return i18n.t('applications.api.update_stage.invalid_stage_progression');
  }

  return i18n.t('applications.api.update_stage.default_error');
};

export const getShortlistCandidatesErrorMessage = (e: any) => {

  if (e?.status === 403) {
    return i18n.t('exceptions.unauthorized');
  }

  if (e?.data?.error_code === BusinessErrorCodes.JobNotVerified) {
    return i18n.t('applications.api.shortlist.not_verified');
  }
  if (e?.data?.error_code === BusinessErrorCodes.ShortlistLimitReached) {
    return i18n.t('applications.api.update_stage.shortlist_limit_reached');
  }
  if (e?.data?.error_code === BusinessErrorCodes.ShortlistDeadlineExpired) {
    return i18n.t('applications.api.update_stage.shortlist_deadline_expired');
  }
  if (e?.data?.error_code === BusinessErrorCodes.NoApplicationsToShortlist) {
    return i18n.t('applications.api.update_stage.no_applications_to_shortlist');
  }


  return i18n.t('applications.api.shortlist.default_error');
};

export const getProgressToCandidateErrorMessage = (e: any) => i18n.t('applications.api.progress_to_candidate.default_error');

export const getRejectApplicationsErrorMessage = (e: any) => i18n.t('applications.api.reject.default_error');

export const getCreateOfferErrorMessage = (e: any) => {

  if (e?.status === 403) {
    return i18n.t('exceptions.unauthorized');
  }

  if (e?.data?.error_code === BusinessErrorCodes.NoVacanciesAvailable) {
    return i18n.t('applications.api.create_offer.no_vacancies');
  }

  if (e?.data?.error_code === BusinessErrorCodes.PendingOffersExist) {
    return i18n.t('applications.api.create_offer.offer_exists');
  }

  return i18n.t('applications.api.create_offer.default_error');
};

export const generateWithdrawApplicationError = (e: any) => {

  if (e?.status === 403) {
    return i18n.t('exceptions.unauthorized');
  }

  if (isString(e?.data?.error_code)) {
    if (e?.data.error_code === BusinessErrorCodes.ApplicationHasFinalized) {
      return i18n.t('applications.api.withdraw.application_finalized');
    }
  }

  return i18n.t('applications.api.withdraw.default_error');
};

export const getResendApplyIntentVerificationErrorMessage = (e: any) => {
  if (e?.data?.error_code === BusinessErrorCodes.NotificationRateLimited) {
    return i18n.t('applications.api.resend_apply_intent_verification.rate_limited');
  }

  return i18n.t('applications.api.update_stage.default_error');
};

export const getSaveToProfileErrorMessage = (e: any) => {
  if (e?.data?.error_code === BusinessErrorCodes.ApplicationDocumentAlreadySaved) {
    return i18n.t('applications.api.save_to_profile.already_saved');
  }

  return i18n.t('applications.api.save_to_profile.default_error');
};


export const getInviteToRepresentErrorMessage = (e: any) => {
  if (e?.data?.error_code === BusinessErrorCodes.ApplicationAlreadyRepresented) {
    return i18n.t('applications.api.invite_to_represent.already_represented');
  }

  if (e?.data?.error_code === BusinessErrorCodes.ApplicationHasFinalized) {
    return i18n.t('applications.api.invite_to_represent.already_represented');
  }

  // job invite related errors
  if (e?.data.error_code === BusinessErrorCodes.CandidateAlreadyApplied) {
    return i18n.t('jobs.api.invite.already_applied');
  }

  if (e?.data.error_code === BusinessErrorCodes.JobInviteExists) {
    return i18n.t('jobs.api.invite.already_invited');
  }

  if (e?.data.error_code === BusinessErrorCodes.JobIsNotActive) {
    return i18n.t('jobs.api.invite.job_not_active');
  }

  if (e?.data.error_code === BusinessErrorCodes.RoleIsNotActive) {
    return i18n.t('jobs.api.invite.job_not_active');
  }

  if (e?.data.error_code === BusinessErrorCodes.AccountTypeConflict) {
    return i18n.t('jobs.api.invite.invalid_recipient');
  }

  return i18n.t('applications.api.invite_to_represent.default_error');
};

export const generateUpdateProfileError = (e: any) => {

  if (e?.status === 403) {
    return i18n.t('exceptions.unauthorized');
  }

  if (isString(e?.data?.error_code)) {
    if (e?.data.error_code === BusinessErrorCodes.ApplicationArchived) {
      return i18n.t('applications.api.update_profile.application_archived');
    }

    if (e?.data.error_code === BusinessErrorCodes.ApplicationAlreadyShortlisted) {
      return i18n.t('applications.api.update_profile.application_shortlisted');
    }
  }

  return i18n.t('applications.api.update_profile.default_error');
};